// reCAPTCHA v3 loader + token helper.
// Loads Google script once on demand. Call executeRecaptcha(action) before
// any POST to a captcha-guarded endpoint.

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
let loadPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (loadPromise) return loadPromise;
  if (!SITE_KEY) {
    console.warn("[recaptcha] REACT_APP_RECAPTCHA_SITE_KEY missing");
    return Promise.reject(new Error("recaptcha_not_configured"));
  }
  loadPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loadPromise = null;
      reject(new Error("recaptcha_script_failed"));
    };
    document.head.appendChild(s);
  });
  return loadPromise;
}

export async function executeRecaptcha(action: string): Promise<string> {
  await loadScript();
  return new Promise((resolve, reject) => {
    if (!window.grecaptcha) return reject(new Error("recaptcha_missing"));
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha!.execute(SITE_KEY, { action });
        resolve(token);
      } catch (err) {
        reject(err);
      }
    });
  });
}

// Preload script (call on mount) so first scan click is instant.
export function preloadRecaptcha() {
  loadScript().catch(() => {});
}
